import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/about')({
  head: () => ({
    meta: [
      { title: 'About / Unilytics' },
      { name: 'description', content: 'Unilytics brings transparency to higher education through objective, verified grade distribution data.' },
    ],
  }),
  component: RouteComponent,
})

const heroStyle = {
  background: 'var(--school-primary)',
  backgroundImage: `
    linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)
  `,
  backgroundSize: '40px 40px',
}

function RouteComponent() {
  return (
    <div style={{ fontFamily: 'var(--font-family-sans)' }}>

      {/* ── Hero ── */}
      <section style={{ ...heroStyle, padding: '72px 0 64px', color: '#fff' }}>
        <div className="max-w-4xl mx-auto px-6">
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 16 }}>
            About Unilytics
          </p>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 20px', color: '#fff' }}>
            Transparency in higher education.
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, maxWidth: 560, margin: 0 }}>
            We believe every student deserves access to the data that helps them make smarter academic decisions.
          </p>
        </div>
      </section>

      {/* ── Mission ── */}
      <section style={{ background: '#fff', padding: '72px 0' }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>Our Mission</SectionLabel>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-0.02em', margin: '0 0 24px' }}>
            Eliminating the information asymmetry in higher education.
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: '#475569', fontSize: 15, lineHeight: 1.8, maxWidth: 680 }}>
            <p style={{ margin: 0 }}>
              Unilytics exists to eliminate the information asymmetry in higher education. For decades, students have made some of the most consequential decisions of their lives — which courses to take, which professors to learn from, which paths to pursue — with almost no reliable data to guide them.
            </p>
            <p style={{ margin: 0 }}>
              We're changing that. By aggregating, standardizing, and analyzing publicly available grade distribution data from universities across the country, we give students the same quality of information that institutions have always had.
            </p>
            <p style={{ margin: 0 }}>
              Our data is sourced exclusively from official university records obtained through public records requests. It is objective, verified, and updated every semester.
            </p>
          </div>
        </div>
      </section>

      {/* ── What We Stand For ── */}
      <section style={{ background: '#F8FAFC', padding: '72px 0' }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>What We Stand For</SectionLabel>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-0.02em', margin: '0 0 40px' }}>
            Three principles we won't compromise on.
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            <PrincipleCard
              icon="📊"
              title="Objective Data Over Opinions"
              body="Every metric on Unilytics is derived from official grade records — not student reviews, not self-reported data, not surveys. If we can't verify it, we don't publish it."
            />
            <PrincipleCard
              icon="🎓"
              title="Transparency For Students"
              body="Students deserve to know what they're signing up for. The Unilytics Difficulty Index (UDI) gives every course a standardized score so you can compare difficulty across courses, professors, and — soon — universities."
            />
            <PrincipleCard
              icon="🏛️"
              title="Institutional Accountability"
              body="When grade outcomes are transparent, institutions compete on quality. We believe public data creates better universities — and better outcomes for students."
            />
          </div>
        </div>
      </section>

      {/* ── UDI Section ── */}
      <section style={{ background: '#0D1B2A', padding: '72px 0', color: '#fff' }}>
        <div className="max-w-4xl mx-auto px-6">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 3, height: 32, borderRadius: 2, background: 'var(--school-primary)', flexShrink: 0 }} />
            <SectionLabel light>The Science Behind the Score</SectionLabel>
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 16px' }}>
            The Unilytics Difficulty Index
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, maxWidth: 640, margin: '0 0 32px' }}>
            The UDI is a 0–10 composite score that combines seven data signals — including withdrawal rate, grade distribution, average GPA, fail rate, class size, professor variance, and semester trends — into a single number. Scores are normalized within each school and designed to be comparable across universities once multi-school data is available.
          </p>
          <a
            href="/faq"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--school-secondary)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(var(--school-secondary-rgb,248,190,0),0.4)',
              paddingBottom: 2,
            }}
          >
            Learn about our methodology →
          </a>
        </div>
      </section>

      {/* ── Data Sources ── */}
      <section style={{ background: '#fff', padding: '72px 0' }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>Where Our Data Comes From</SectionLabel>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-0.02em', margin: '0 0 24px' }}>
            Official records. Nothing else.
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: '#475569', fontSize: 15, lineHeight: 1.8, maxWidth: 680 }}>
            <p style={{ margin: 0 }}>
              All grade distribution data on Unilytics is sourced from public university records obtained through Freedom of Information Act (FOIA) requests and publicly available institutional research publications.
            </p>
            <p style={{ margin: 0 }}>
              Data is processed, standardized, and validated before publication. We do not publish data that cannot be verified against official university sources.
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: '#0D1B2A' }}>Current coverage:</strong> University of Houston (2013–present). Expanding to additional universities in 2025.
            </p>
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section style={{ background: '#F8FAFC', padding: '72px 0' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionLabel>Get In Touch</SectionLabel>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
            Questions? We'd love to hear from you.
          </h2>
          <p style={{ fontSize: 15, color: '#64748B', lineHeight: 1.7, maxWidth: 480, margin: '0 auto 32px' }}>
            Have a question, want to suggest your university, or interested in a data partnership?
          </p>
          <a
            href="mailto:hello@unilytics.io"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 15,
              fontWeight: 700,
              color: '#fff',
              background: 'var(--school-primary)',
              padding: '12px 28px',
              borderRadius: 10,
              textDecoration: 'none',
              letterSpacing: '0.01em',
            }}
          >
            Contact Us →
          </a>
        </div>
      </section>

    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function SectionLabel({ children, light }: { children: React.ReactNode; light?: boolean }) {
  return (
    <p style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: light ? 'rgba(255,255,255,0.4)' : 'var(--school-primary)',
      margin: '0 0 12px',
    }}>
      {children}
    </p>
  )
}

function PrincipleCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '28px 24px',
      border: '1px solid rgba(13,27,42,0.07)',
      boxShadow: '0 2px 12px rgba(13,27,42,0.05)',
    }}>
      <div style={{ fontSize: 28, marginBottom: 14 }}>{icon}</div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', margin: '0 0 10px', lineHeight: 1.3 }}>
        {title}
      </h3>
      <p style={{ fontSize: 13.5, color: '#64748B', lineHeight: 1.7, margin: 0 }}>
        {body}
      </p>
    </div>
  )
}
